import { Box, Flex, Text } from '@chakra-ui/react';
import {
  OrganizationPartnerItemFragment,
  PartnerPreferenceType,
  PartnerType,
} from '@client/graphql/__generated__/types';
import { Select } from '@document/common/Select';
import { GroupBase } from 'chakra-react-select';
import { memo, useMemo } from 'react';
import { PREFERENCE_TYPE_LABELS, RosterPreference } from './types';

interface PreferredPartnerSelectProps {
  partnerType: PartnerType;
  preference: RosterPreference | null;
  partners: OrganizationPartnerItemFragment[];
  onChange: (
    preferenceType: PartnerPreferenceType,
    partnerId?: string | null,
    partnerContactId?: string | null
  ) => void;
  isDisabled?: boolean;
}

interface OptionValue {
  preferenceType: PartnerPreferenceType;
  partnerId?: string;
  partnerContactId?: string;
  label: string;
  partnerName?: string;
  contactName?: string;
}

const selectStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    width: '100%',
  }),
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    bg: 'whiteAlpha.50',
    border: '1px solid',
    borderColor: 'whiteAlpha.100',
    minHeight: 'auto',
    cursor: 'pointer',
    py: 1,
    px: 2,
    _hover: { borderColor: 'whiteAlpha.200' },
  }),
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    px: 3,
  }),
  dropdownIndicator: () => ({
    pr: 2,
    py: 1,
    color: 'whiteAlpha.600',
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    bg: 'indigo.800',
    border: '1px solid',
    borderColor: 'whiteAlpha.200',
    overflow: 'hidden',
    minWidth: '200px',
    zIndex: 10,
  }),
  group: (provided: Record<string, unknown>) => ({
    ...provided,
    p: 0,
  }),
  groupHeading: (provided: Record<string, unknown>) => ({
    ...provided,
    bg: 'whiteAlpha.50',
    color: 'whiteAlpha.600',
    fontSize: 'xs',
    fontWeight: 'medium',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    py: 2,
    px: 3,
    mb: 0,
  }),
  option: (
    provided: Record<string, unknown>,
    state: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...provided,
    bg: state.isSelected
      ? 'whiteAlpha.200'
      : state.isFocused
        ? 'whiteAlpha.100'
        : 'transparent',
    color: 'white',
    fontSize: 'sm',
    py: 2,
    px: 3,
    cursor: 'pointer',
    _hover: { bg: 'whiteAlpha.100' },
  }),
};

export const PreferredPartnerSelect = memo(function PreferredPartnerSelect({
  partnerType,
  preference,
  partners,
  onChange,
  isDisabled,
}: PreferredPartnerSelectProps) {
  const options = useMemo(() => {
    const groups: GroupBase<OptionValue>[] = [];

    // Rotation options
    const rotationOptions: OptionValue[] = [
      {
        preferenceType: PartnerPreferenceType.ROTATION,
        label: 'All Partners',
      },
      ...partners.map((p) => ({
        preferenceType: PartnerPreferenceType.ROTATION,
        partnerId: p.id,
        label: p.name,
        partnerName: p.name,
      })),
    ];

    groups.push({
      label:
        PREFERENCE_TYPE_LABELS[PartnerPreferenceType.ROTATION][partnerType],
      options: rotationOptions,
    });

    // Set Per Transaction
    groups.push({
      label:
        PREFERENCE_TYPE_LABELS[PartnerPreferenceType.SET_PER_TRANSACTION][
          partnerType
        ],
      options: [
        {
          preferenceType: PartnerPreferenceType.SET_PER_TRANSACTION,
          label: 'Set Per Transaction',
        },
      ],
    });

    return groups;
  }, [partners, partnerType]);

  const selectedValue = useMemo((): OptionValue | null => {
    if (!preference) {
      // Default to Rotation - All Partners
      return {
        preferenceType: PartnerPreferenceType.ROTATION,
        label: 'All Partners',
      };
    }

    if (
      preference.preferenceType === PartnerPreferenceType.SET_PER_TRANSACTION
    ) {
      return {
        preferenceType: PartnerPreferenceType.SET_PER_TRANSACTION,
        label: 'Set Per Transaction',
      };
    }

    if (preference.preferenceType === PartnerPreferenceType.SPECIFIC_CONTACT) {
      const contact = preference.partnerContact;
      if (contact) {
        return {
          preferenceType: PartnerPreferenceType.SPECIFIC_CONTACT,
          partnerContactId: contact.id,
          partnerId: contact.partner?.id,
          label: `${contact.firstName} ${contact.lastName}`,
          contactName: `${contact.firstName} ${contact.lastName}`,
          partnerName: contact.partner?.name,
        };
      }
    }

    if (preference.preferenceType === PartnerPreferenceType.ROTATION) {
      if (preference.partner) {
        return {
          preferenceType: PartnerPreferenceType.ROTATION,
          partnerId: preference.partner.id,
          label: preference.partner.name,
          partnerName: preference.partner.name,
        };
      }

      return {
        preferenceType: PartnerPreferenceType.ROTATION,
        label: 'All Partners',
      };
    }

    return null;
  }, [preference]);

  return (
    <Box maxWidth="220px" minWidth="180px">
      <Select<OptionValue, false, GroupBase<OptionValue>>
        chakraStyles={selectStyles}
        components={{
          SingleValue: ({ data }) => (
            <Flex direction="column" gap={0.5} gridArea="1/1/2/3">
              <Text color="white" fontSize="sm" fontWeight="500">
                {PREFERENCE_TYPE_LABELS[data.preferenceType][partnerType]}
              </Text>
              {data.partnerName && (
                <Flex alignItems="center" gap={2}>
                  <Box borderRadius="4px">
                    <Text color="whiteAlpha.700" fontSize="sm">
                      {data.partnerName}
                    </Text>
                  </Box>
                  {data.contactName && (
                    <Text color="whiteAlpha.700" fontSize="sm">
                      {data.contactName}
                    </Text>
                  )}
                </Flex>
              )}
              {!data.partnerName &&
                data.preferenceType === PartnerPreferenceType.ROTATION && (
                  <Text color="whiteAlpha.400" fontSize="sm">
                    All Partners
                  </Text>
                )}
            </Flex>
          ),
        }}
        getOptionLabel={(opt) => opt.label}
        getOptionValue={(opt) =>
          `${opt.preferenceType}-${opt.partnerId ?? ''}-${
            opt.partnerContactId ?? ''
          }`
        }
        isDisabled={isDisabled}
        isSearchable={true}
        menuPlacement="auto"
        menuPortalTarget={document.body}
        options={options}
        value={selectedValue}
        onChange={(newValue) => {
          if (newValue) {
            onChange(
              newValue.preferenceType,
              newValue.partnerId ?? null,
              newValue.partnerContactId ?? null
            );
          }
        }}
      />
    </Box>
  );
});
